import { chunk, uniq } from "lodash";

// Typesense filter values must be backtick-wrapped if they contain filter metacharacters.
// adidas article numbers are plain 6-char alphanumerics today; this is defensive.
const NEEDS_ESCAPE = /[,()[\]&|`\s]/;

export const escapeFilterValue = (value) => {
  const text = String(value);
  return NEEDS_ESCAPE.test(text) ? `\`${text}\`` : text;
};

/**
 * Search params for the catalogue picker's all-seasons search.
 *
 * shallow (1-2 characters typed) is a PREVIEW. The saving comes from exhaustive_search:false, not
 * from per_page — a short prefix query's cost is dominated by expansion (max_candidates: 4 vs
 * 10000), so a non-exhaustive query is cheap regardless of page size. The trade is that it returns
 * an arbitrary subset of expansions and under-reports `found`, which is why it is labelled a
 * preview in the UI.
 *
 * typo_tokens_threshold is deliberately absent: Typesense ignores it when exhaustive_search is on.
 */
export const buildAllSeasonsSearchParams = ({ term, activeSeasonKeys, shallow }) => {
  assertSingleRegion(activeSeasonKeys);

  return {
    q: term,
    query_by: "articleNumber, articleName",
    filter_by: `seasons:=[${(activeSeasonKeys || []).join(",")}]`,
    per_page: shallow ? 40 : 250,
    exhaustive_search: !shallow,
    num_typos: 0
  };
};

/**
 * `searches` array for multiSearch.perform — used to complete variant sets when the main search was
 * truncated. Must go through multi_search (POST): a GET filter string would exceed Typesense's
 * 4000-character query cap.
 *
 * chunkSize is 100, not 250, because per_page caps DOCUMENTS and one article number can carry
 * several records — a 250-number chunk under per_page 250 would truncate exactly the case this
 * function exists to fix.
 *
 * The active-season clause is REQUIRED: without it this pulls records whose seasons are all
 * inactive, which produce no chip and reopen the inactive-season search that is deliberately closed.
 */
export const buildArticleNumberSearches = ({
  articleNumbers,
  activeSeasonKeys,
  collectionName,
  chunkSize = 100
}) => {
  const numbers = uniq(articleNumbers || []);
  if (numbers.length === 0) return [];

  const seasonClause = `seasons:=[${(activeSeasonKeys || []).join(",")}]`;

  return chunk(numbers, chunkSize).map((group) => ({
    collection: collectionName,
    filter_by: `articleNumber:=[${group.map(escapeFilterValue).join(",")}] && ${seasonClause}`
  }));
};

// One Firebase project and one Typesense collection serve both regions; isolation rests entirely on
// bookingActiveSeasons being single-region. If a US season is ever activated into it, chips would
// mix CAD and USD prices and present a currency difference as a seasonal price choice.
export const assertSingleRegion = (activeSeasonKeys) => {
  const regions = new Set(
    (activeSeasonKeys || [])
      .map((key) => key.split("_")[1])
      .filter((region) => region != null)
  );
  if (regions.size > 1) {
    console.error(
      `Active seasons span multiple regions (${[...regions].join(
        ", "
      )}). Catalogue picker prices will mix currencies.`
    );
  }
};
